import React, { useState, useEffect, useMemo } from 'react';
import { invoicesAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Receipt, Search, ArrowUp, ArrowDown, ArrowUpDown, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import logger from '@/lib/logger';

const getMonthLabel = (period) => {
  if (!period || period === '-') return '-';
  if (/^\d{4}-\d{2}$/.test(period)) {
    const [yr, m] = period.split('-');
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${names[parseInt(m) - 1]}'${yr.slice(2)}`;
  }
  const match = period.match(/^([a-z]+)_(\d{4})$/i);
  if (match) {
    const mNames = { jan:'Jan', feb:'Feb', mar:'Mar', apr:'Apr', may:'May', jun:'Jun', jul:'Jul', aug:'Aug', sep:'Sep', oct:'Oct', nov:'Nov', dec:'Dec' };
    return `${mNames[match[1].toLowerCase()] || match[1]}'${match[2].slice(2)}`;
  }
  return period;
};

export default function PendingInvoicesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [boardFilter, setBoardFilter] = useState('all');
  const [sort, setSort] = useState({ col: 'client_name', dir: 'asc' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    invoicesAPI.uninvoicedSubtasks()
      .then(res => setItems(res.data || []))
      .catch(err => logger.error('Failed to load uninvoiced subtasks', err))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- runs once on mount; API refs are stable

  const clients = useMemo(() => {
    const map = {};
    for (const item of items) {
      if (item.client_id && item.client_name) map[item.client_id] = item.client_name;
    }
    return Object.entries(map).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const boards = useMemo(() => {
    const map = {};
    for (const item of items) {
      if (item.board_id && item.board_name) map[item.board_id] = item.board_name;
    }
    return Object.entries(map).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const filteredItems = useMemo(() => {
    let result = items;
    if (clientFilter !== 'all') result = result.filter(i => i.client_id === clientFilter);
    if (boardFilter !== 'all') result = result.filter(i => i.board_id === boardFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(i =>
        (i.client_name || '').toLowerCase().includes(q) ||
        (i.subtask_title || '').toLowerCase().includes(q) ||
        (i.board_name || '').toLowerCase().includes(q)
      );
    }
    const { col, dir } = sort;
    return [...result].sort((a, b) => {
      let va = a[col], vb = b[col];
      if (col === 'filed_count') {
        va = a.filed_periods?.length || 0;
        vb = b.filed_periods?.length || 0;
        return dir === 'asc' ? va - vb : vb - va;
      }
      va = (va || '').toString().toLowerCase();
      vb = (vb || '').toString().toLowerCase();
      return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [items, clientFilter, boardFilter, searchQuery, sort]);

  const toggleSort = (col) => {
    setSort(prev => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-muted rounded-xl animate-pulse" />)}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
        </Button>
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Receipt className="h-5 w-5 text-violet-600" />
            Pending Invoicing
            <Badge variant="secondary" className="ml-1 bg-violet-100 text-violet-700">{filteredItems.length}</Badge>
          </h2>
          <p className="text-sm text-muted-foreground">Filed subtasks not yet invoiced</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-[280px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input placeholder="Search client, subtask, board..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-8 h-8 text-xs" data-testid="pending-inv-search" />
        </div>
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="pending-inv-client-filter"><SelectValue placeholder="All Clients" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clients</SelectItem>
            {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={boardFilter} onValueChange={setBoardFilter}>
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="pending-inv-board-filter"><SelectValue placeholder="All Boards" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Boards</SelectItem>
            {boards.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {(searchQuery || clientFilter !== 'all' || boardFilter !== 'all') && (
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setSearchQuery(''); setClientFilter('all'); setBoardFilter('all'); }}>Clear</Button>
        )}
        <span className="text-[11px] text-muted-foreground ml-auto">{filteredItems.length} item(s)</span>
      </div>

      {/* Table */}
      {filteredItems.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                {[
                  { key: 'client_name', label: 'Client' },
                  { key: 'subtask_title', label: 'Subtask' },
                  { key: 'board_name', label: 'Board' },
                  { key: 'filed_count', label: 'Filed Periods' },
                  { key: 'from_period', label: 'Period Range' },
                ].map(col => (
                  <TableHead key={col.key} className="text-xs font-semibold cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort(col.key)}>
                    <span className="flex items-center gap-1">
                      {col.label}
                      {sort.col === col.key ? (sort.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground/40" />}
                    </span>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((item, idx) => (
                <TableRow key={`${item.subtask_id || item.client_id}-${idx}`} className="hover:bg-muted/30 cursor-pointer" onClick={() => navigate('/dashboard/billing')} data-testid={`pending-inv-row-${idx}`}>
                  <TableCell className="text-sm font-medium">{item.client_name || item.client_id?.slice(0, 8)}</TableCell>
                  <TableCell className="text-sm">{item.subtask_title}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{item.board_name}</TableCell>
                  <TableCell className="text-xs text-center">{item.filed_periods?.length || 0}</TableCell>
                  <TableCell className="text-xs">{item.from_period ? `${getMonthLabel(item.from_period)} - ${getMonthLabel(item.to_period)}` : '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No pending invoicing items found</CardContent></Card>
      )}
    </div>
  );
}
